import { t } from '@stokr/components-library'
import type { CopyKey } from 'locales/copy-keys.gen'
import { useAuth, type SessionUser } from 'shared/context/AuthContext'
import { usePrivateInvestorProjects, type PrivateInvestorProjectStatus } from 'hooks/usePrivateInvestor'
import { useStoProject } from './useStoProject'
import { getAuthState, type AuthState } from 'shared/utils/authState'
import { resolveSectionState, type SectionState } from 'models/stoSection'
import type { Project } from 'models/project'
import {
  MARKET_TYPES,
  getUserProjectEligibility,
  isPrimaryMarketAvailable,
  EligibilityItemState,
  type MarketType,
  type ProjectEligibility,
  type EligibilityItem,
  type EligibilityOptions,
  type EligibilityProject,
  type EligibilityUser,
  type ModalType,
  MODAL_TYPES,
} from 'shared/domain/projectEligibility'

/**
 * Everything the STO page needs to turn the eligibility brain into UI.
 *
 * The brain — `shared/domain/projectEligibility` — is the source of truth and knows
 * nothing about React or copy. This file is the STO page's side of that: it reads the
 * session and the project out of React, runs the brain, and shapes the result into the
 * two responses the page renders — the "Eligibility check" footer and the "How can I
 * invest?" checklist. Two responses, one shared brain, one file.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Shared wiring — the bridge from React (auth + project cache) to the brain.
// Private on purpose: it only exists so the two view hooks below don't repeat it.
// Not a util — it calls hooks (`useAuth`, `useStoProject`) and so must be one.
// ─────────────────────────────────────────────────────────────────────────────

/** Trading availability as the brain expects it (upstream field: `tradingContent`). */
export interface EligibilityTradingContent {
  isAvailableForTrading?: boolean
}

/**
 * Casts homepage's own models to the brain's shapes. Unavoidable: `Project`/`SessionUser`
 * are not structurally assignable upstream (different `ProjectSale`, an index signature),
 * so TS can't verify field-by-field.
 *
 * `tradingContent` is the trap. Homepage stores that record as `tradingData` — a different
 * name — so it is absent from `Project`, and the secondary-market brain reads
 * `tradingContent?.isAvailableForTrading` to decide whether a secondary market exists at
 * all. The SECONDARY branch merges it explicitly; a raw project would silently report
 * every user unable to trade. That is why the merge lives here and cannot be skipped.
 */
const toEligibilityUser = (
  user: SessionUser | null | undefined,
  privateInvestorProjects?: PrivateInvestorProjectStatus[],
): EligibilityUser | null | undefined => {
  if (!user) return user
  const cast = user as unknown as EligibilityUser
  return privateInvestorProjects ? { ...cast, privateInvestorProjects } : cast
}

const toEligibilityProject = (
  project: Project | undefined,
  market: MarketType,
  tradingContent: EligibilityTradingContent | null | undefined,
): EligibilityProject | undefined => {
  if (!project) return undefined
  if (market === MARKET_TYPES.SECONDARY) {
    return { ...project, tradingContent } as unknown as EligibilityProject
  }
  return project as unknown as EligibilityProject
}

interface EligibilityResult {
  authState: AuthState
  isPending: boolean
  /** Auth not settled, or signed in with the profile / private-investor statuses still
   *  loading. The brain would count the missing data as "not done", so callers wait. */
  isResolving: boolean
  isError: boolean
  /** Project cast to the eligibility shape, for callers reading its flags directly (the
   *  signed-out preview). Undefined until the project query resolves. */
  project: EligibilityProject | undefined
  /** `undefined` until authenticated — the brain can't evaluate anything without a user,
   *  so callers decide what to show before then (a preview, a sign-in prompt). */
  eligibility: ProjectEligibility | undefined
}

/**
 * True while eligibility cannot be trusted yet. `isLoading`, not `isPending`, for the
 * statuses request: a query that never runs (no user id) stays pending forever.
 */
export const isEligibilityResolving = (
  authState: AuthState,
  isFetchingUser: boolean,
  isLoadingPrivateInvestor: boolean,
): boolean =>
  authState === 'resolving' || (authState === 'authenticated' && (isFetchingUser || isLoadingPrivateInvestor))

/**
 * Resolves PRIMARY or SECONDARY eligibility for the current visitor. Parameterized by
 * market because the brain already takes `market` as an argument — Trade's future
 * secondary check is this same helper with `MARKET_TYPES.SECONDARY`, not another file.
 */
function useResolvedEligibility(
  market: MarketType,
  tradingContent?: EligibilityTradingContent | null,
  options?: EligibilityOptions,
): EligibilityResult {
  const { user, isFetchingUser } = useAuth()
  const { project, isPending, isError } = useStoProject()
  const authState = getAuthState(user)

  const privateInvestorQuery = usePrivateInvestorProjects({
    userId: user?._id,
    enabled: authState === 'authenticated',
  })
  const privateInvestorProjects = privateInvestorQuery.data
  const isResolving = isEligibilityResolving(authState, isFetchingUser, privateInvestorQuery.isLoading)

  const eligibilityProject = toEligibilityProject(project, market, tradingContent)

  const eligibility =
    authState === 'authenticated'
      ? getUserProjectEligibility(
          toEligibilityUser(user, privateInvestorProjects),
          eligibilityProject,
          market,
          undefined,
          options,
        )
      : undefined

  return { authState, isPending, isError, isResolving, project: eligibilityProject, eligibility }
}

/**
 * The gate for the Invest action: is the visitor signed in, and are they fully eligible
 * for the primary market. `isEligible` is true only when every required step is done —
 * the fail-closed default the Invest button needs.
 */
export function useInvestEligibility(): {
  /** The product is open for investment: product data only, the same for every visitor.
   *  Undefined until the project has loaded, so nothing claims "closed" meanwhile. */
  isOpen: boolean | undefined
  isLoggedIn: boolean
  isEligible: boolean
  isResolving: boolean
} {
  const { authState, eligibility, isResolving, project } = useResolvedEligibility(MARKET_TYPES.PRIMARY)
  return {
    isOpen: project ? isPrimaryMarketAvailable(project) : undefined,
    isLoggedIn: authState === 'authenticated',
    isEligible: !isResolving && eligibility?.status === EligibilityItemState.DONE,
    isResolving,
  }
}

/**
 * The gate for trading on SideSwap: fully eligible for the secondary market. Bitfinex has
 * its own onboarding, so it needs nothing from STOKR and does not use this.
 */
export function useTradeEligibility(tradingContent: EligibilityTradingContent | null | undefined): {
  isEligible: boolean
  isResolving: boolean
} {
  const { eligibility, isResolving } = useResolvedEligibility(MARKET_TYPES.SECONDARY, tradingContent)
  return { isEligible: !isResolving && eligibility?.status === EligibilityItemState.DONE, isResolving }
}

// ─────────────────────────────────────────────────────────────────────────────
// Response 1 — the "Eligibility check" footer under the Invest/Trade/Redeem card.
// A single summary line + at most one action.
// ─────────────────────────────────────────────────────────────────────────────

/** What the "Eligibility check" panel shows. No `cta` means there is nothing to do. */
export interface EligibilityCheckView {
  message: string
  /** A lighter line under the message. */
  detail?: string
  /** At most one of `link` / `opensLoginModal` / `opensModal` is set. */
  cta?: { label: string; link?: string; opensLoginModal?: boolean; opensModal?: ModalType }
  /** Outstanding steps, for the "N steps left" message. */
  stepsRemaining: number
}

/** The step modals the STO page can open in place. */
export const STO_STEP_MODALS: readonly ModalType[] = [
  MODAL_TYPES.PRIVATE_INVESTOR,
  MODAL_TYPES.EU_PROFESSIONAL_INVESTOR,
]

/** The footer's wording per market: Invest (and Redeem) speak of investing, Trade of trading. */
const CHECK_COPY: Record<MarketType, { signedOut: CopyKey; oneStepLeft: CopyKey; stepsLeft: CopyKey }> = {
  [MARKET_TYPES.PRIMARY]: {
    signedOut: 'stoPage.keyActions.eligibility.signedOut',
    oneStepLeft: 'stoPage.keyActions.eligibility.oneStepLeft',
    stepsLeft: 'stoPage.keyActions.eligibility.stepsLeft',
  },
  [MARKET_TYPES.SECONDARY]: {
    signedOut: 'stoPage.keyActions.eligibility.trade.signedOut',
    oneStepLeft: 'stoPage.keyActions.eligibility.trade.oneStepLeft',
    stepsLeft: 'stoPage.keyActions.eligibility.trade.stepsLeft',
  },
}

/** `Unavailable. Explore other products.` → the first sentence as the message, the rest as its detail. */
export const splitFirstSentence = (text: string): [string, string | undefined] => {
  const match = /^(.+?[.!?])\s+(\S.*)$/s.exec(text.trim())
  return match ? [match[1], match[2]] : [text, undefined]
}

/**
 * Turns a resolved eligibility into the footer's message + action.
 *
 * "Steps remaining" counts only NOT_DONE items, matching upstream's `calculateButtonStates`
 * — an item merely under review is not actionable, so counting it would tell the user to
 * do work that does not exist.
 */
export const toEligibilityCheckView = (eligibility: ProjectEligibility): EligibilityCheckView | null => {
  // Nothing to say when fully eligible — the Invest panel already shows the "Active" state.
  if (eligibility.status === EligibilityItemState.DONE) {
    return null
  }

  if (eligibility.status === EligibilityItemState.IN_PROGRESS) {
    return { message: t('stoPage.keyActions.eligibility.underReview'), stepsRemaining: 0 }
  }

  // The market itself is closed (no steps at all): the panel's notice already says so.
  if (eligibility.status === EligibilityItemState.NOT_AVAILABLE && eligibility.items.length === 0) {
    return null
  }

  if (
    eligibility.status === EligibilityItemState.NOT_AVAILABLE ||
    eligibility.status === EligibilityItemState.NOT_ALLOWED
  ) {
    // Upstream already supplies the reason; fall back only if it did not.
    const [message, detail] = splitFirstSentence(eligibility.tooltip || t('stoPage.keyActions.eligibility.unavailable'))
    return { message, detail, stepsRemaining: 0 }
  }

  const outstanding = (eligibility.missing ?? []).filter((item) => item.status === EligibilityItemState.NOT_DONE)
  const stepsRemaining = outstanding.length
  const copy = CHECK_COPY[eligibility.marketType] ?? CHECK_COPY[MARKET_TYPES.PRIMARY]
  // The first step there is somewhere to go for, in checklist order, as HowToInvest offers it.
  const next = outstanding.find(
    (i) => (i.showLink && i.link) || (i.openModal && i.modal && STO_STEP_MODALS.includes(i.modal)),
  )
  const opensModal = next && !(next.showLink && next.link) ? next.modal : undefined

  return {
    message: stepsRemaining === 1 ? t(copy.oneStepLeft) : t(copy.stepsLeft, { count: stepsRemaining }),
    cta: next
      ? {
          label: t('stoPage.keyActions.eligibility.complete'),
          ...(opensModal ? { opensModal } : { link: next.link }),
        }
      : undefined,
    stepsRemaining,
  }
}

/**
 * Drives the "Eligibility check" footer for one market: primary under Invest and Redeem,
 * secondary under Trade. `loading` until eligibility can be trusted; `hidden` when fully
 * eligible. The steps are listed whether or not the market is open: the panel's notice says
 * when it is closed, and the visitor can finish onboarding beforehand.
 */
export function useEligibilityCheckPanel(
  market: MarketType = MARKET_TYPES.PRIMARY,
): SectionState<EligibilityCheckView> {
  const { eligibility, isResolving } = useResolvedEligibility(market, undefined, { ignoreMarketAvailability: true })

  if (isResolving) return { status: 'loading' }

  if (!eligibility) {
    return {
      status: 'ready',
      data: {
        message: t(CHECK_COPY[market].signedOut),
        // Opens the login modal in place, same as HowToInvest's sign-in step.
        cta: { label: t('stoPage.keyActions.eligibility.signIn'), opensLoginModal: true },
        stepsRemaining: 0,
      },
    }
  }

  const view = toEligibilityCheckView(eligibility)
  return view ? { status: 'ready', data: view } : { status: 'hidden' }
}

// ─────────────────────────────────────────────────────────────────────────────
// Response 2 — the "How can I invest?" checklist. A full, ordered step list.
// ─────────────────────────────────────────────────────────────────────────────

/** How a step's badge renders. `start` is the only one the visitor can act on. */
export type HowToInvestStepState = 'start' | 'locked' | 'inProgress' | 'done'

export interface HowToInvestStep {
  key: string
  title: string
  description?: string
  state: HowToInvestStepState
  /** Present only when the step is actionable. */
  link?: string
  /** Which modal completes this step, when it is completed in-page rather than by link. */
  modal?: ModalType
}

/** The first step: opens the login modal in place rather than linking away. */
export const SIGN_IN_STEP_KEY = 'sign-in'

/**
 * Signed-out preview only.
 *
 * The brain returns NO items without a user, but the design still lists the journey for a
 * signed-out visitor with every step after "Sign in" locked. So this derives the *shape*
 * of that list from project flags alone. The two conditionals mirror the brain exactly —
 * self-certification when `requiresTaxId`, private investor when `only_private_investors`.
 * If those rules change upstream, change them here too.
 *
 * ⚠️ PREVIEW for display only. Never used to decide whether someone may invest — that
 * always goes through the brain, which needs a user.
 */
const buildSignedOutInvestSteps = (project: EligibilityProject | undefined): HowToInvestStep[] => {
  const steps: HowToInvestStep[] = [
    {
      key: SIGN_IN_STEP_KEY,
      title: t('stoPage.howToInvest.signIn.title'),
      description: t('stoPage.howToInvest.signIn.description'),
      state: 'start',
      // No link: the component opens the login modal on this page.
    },
    {
      key: 'kyc',
      title: t('generateCoreChecklistTasks.identity.title.individual'),
      description: t('generateCoreChecklistTasks.identity.description.missing.individual'),
      state: 'locked',
    },
    {
      key: 'wallet',
      title: t('generateCoreChecklistTasks.wallet.title'),
      description: t('generateCoreChecklistTasks.wallet.description.missing'),
      state: 'locked',
    },
  ]

  if (project?.requiresTaxId) {
    steps.push({
      key: 'selfCertification',
      title: t('generateCoreChecklistTasks.selfCertification.title'),
      description: t('generateCoreChecklistTasks.selfCertification.description.missing'),
      state: 'locked',
    })
  }

  if (project?.only_private_investors) {
    steps.push({
      key: 'private-investor',
      title: t('projectEligibility.privateInvestor.name'),
      description: t('projectEligibility.privateInvestor.descriptionNotSubmitted'),
      state: 'locked',
    })
  }

  return steps
}

/** Eligibility statuses are about *whether you qualify*; this maps them to badge states. */
export const toInvestStepState = (item: EligibilityItem): HowToInvestStepState => {
  if (item.status === EligibilityItemState.DONE) return 'done'
  if (item.status === EligibilityItemState.IN_PROGRESS) return 'inProgress'
  // NOT_AVAILABLE / NOT_ALLOWED can't be acted on; NOT_DONE only if there's somewhere to go.
  if (item.status === EligibilityItemState.NOT_AVAILABLE || item.status === EligibilityItemState.NOT_ALLOWED) {
    return 'locked'
  }
  return item.showLink || item.openModal ? 'start' : 'locked'
}

export const toHowToInvestSteps = (items: EligibilityItem[]): HowToInvestStep[] =>
  items.map((item) => ({
    key: item.key,
    title: item.name,
    description: item.description || undefined,
    state: toInvestStepState(item),
    link: item.showLink ? item.link : undefined,
    modal: item.modal,
  }))

/**
 * "How can I invest?" — the primary-market checklist. Signed-in visitors get their real
 * statuses from the brain; signed-out visitors get the locked preview above. A skeleton
 * shows until eligibility can be trusted, so a just-signed-in visitor never sees steps
 * flash as "not done".
 */
export function useHowToInvest(): SectionState<HowToInvestStep[]> {
  // Listed even while the sale is closed: the user can finish onboarding beforehand, and the
  // Invest panel already says the investment is closed.
  const { isPending, isError, isResolving, project, eligibility } = useResolvedEligibility(
    MARKET_TYPES.PRIMARY,
    undefined,
    { ignoreMarketAvailability: true },
  )

  const steps = eligibility ? toHowToInvestSteps(eligibility.items) : buildSignedOutInvestSteps(project)

  return resolveSectionState({
    isPending: isPending || isResolving,
    isError,
    data: steps,
    isEmpty: (value) => value.length === 0,
  })
}
